import { useCallback } from "react";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { storage } from "@data/firebase";
import { useAuth } from "@contexts/AuthContext";
import { useProjects } from "@contexts/ProjectContext";
import { projectStorageBasePath } from "@domain/project/paths";
import useFirestoreDoc from "@data/hooks/useFirestoreDoc";
import { resizeImage } from "@shared/utils/imageResize";

/**
 * Manages feedback tickets stored at users/{uid}/data/feedback-tickets.
 *
 * Each ticket has:
 *   - status: 'open' | 'done' | 'cancelled'
 *   - closeReason: 'done' | 'cancelled' | null
 *   - comments: [{ id, author, text, imageUrl?, createdAt }]
 *
 * Images are uploaded to Storage at
 *   users/{uid}/feedback/{ticketId}/{index}.jpg
 *   users/{uid}/feedback/{ticketId}/comment-{commentId}.jpg
 */
export default function useFeedbackTickets() {
  const { user } = useAuth();
  const { activeProject } = useProjects();
  const [tickets, setTickets] = useFirestoreDoc("feedback-tickets", []);

  const createTicket = useCallback(
    async ({ description, contractorId, files = [] }) => {
      if (!user || !activeProject) return;
      const ticketId = crypto.randomUUID();

      const images = [];
      for (let i = 0; i < files.length; i++) {
        const resized = await resizeImage(files[i], 1600, 0.85);
        const path = `${projectStorageBasePath(activeProject)}/feedback/${ticketId}/${i}.jpg`;
        const sref = storageRef(storage, path);
        await uploadBytes(sref, resized);
        const url = await getDownloadURL(sref);
        images.push({
          id: crypto.randomUUID(),
          imageUrl: url,
          storagePath: path,
        });
      }

      const ticket = {
        id: ticketId,
        status: "open",
        description,
        contractorId,
        images,
        comments: [],
        createdAt: Date.now(),
        closedAt: null,
        closeReason: null,
        closeMessage: "",
      };

      setTickets((prev) => [ticket, ...(prev || [])]);
      return ticket;
    },
    [user, activeProject, setTickets]
  );

  /**
   * Close a ticket with a reason ('done' or 'cancelled') and optional message.
   */
  const closeTicket = useCallback(
    (ticketId, reason = "done", message = "") => {
      setTickets((prev) =>
        (prev || []).map((t) =>
          t.id === ticketId
            ? {
                ...t,
                status: reason, // 'done' or 'cancelled'
                closedAt: Date.now(),
                closeReason: reason,
                closeMessage: message,
              }
            : t
        )
      );
    },
    [setTickets]
  );

  const reopenTicket = useCallback(
    (ticketId) => {
      setTickets((prev) =>
        (prev || []).map((t) =>
          t.id === ticketId
            ? {
                ...t,
                status: "open",
                closedAt: null,
                closeReason: null,
                closeMessage: "",
              }
            : t
        )
      );
    },
    [setTickets]
  );

  /**
   * Add a comment to a ticket's thread. Optionally attach one image.
   */
  const addComment = useCallback(
    async (ticketId, { text, author = "Owner", file = null }) => {
      if (!user || !activeProject || !text?.trim()) return;
      const commentId = crypto.randomUUID();

      let imageUrl = null;
      let storagePath = null;
      if (file) {
        const resized = await resizeImage(file, 1200, 0.85);
        const path = `${projectStorageBasePath(activeProject)}/feedback/${ticketId}/comment-${commentId}.jpg`;
        const sref = storageRef(storage, path);
        await uploadBytes(sref, resized);
        imageUrl = await getDownloadURL(sref);
        storagePath = path;
      }

      const comment = {
        id: commentId,
        author,
        text: text.trim(),
        imageUrl,
        storagePath,
        createdAt: Date.now(),
      };

      setTickets((prev) =>
        (prev || []).map((t) =>
          t.id === ticketId
            ? { ...t, comments: [...(t.comments || []), comment] }
            : t
        )
      );
    },
    [user, activeProject, setTickets]
  );

  return {
    tickets: tickets || [],
    createTicket,
    closeTicket,
    reopenTicket,
    addComment,
  };
}
